import { HTTPServiceClient } from '@/packages/http/service';
import * as endpoint from './endpoints';
import {
  Response,
  SubmissionRequestSendData,
  AddFundRequest,
  Strategy,
  ProductType,
  AddNewPortfolioRequest,
  DeleteFundRequest,
  DeletePortfolioRequest,
  CostTemplateResponse,
  PaginatedResult,
  OrgFundsRequest,
  PensionSchemeRequest,
  SubFundRequest,
  OrganisationManager,
  BulkTemplateResponse,
  AddFundExtrasRequest,
  Currency
} from './types';
import { FundRequestPayload, PortfolioRequestPayload } from './service-helper';
import { PensionScheme } from '../pensionScheme/types';
import { SubFund } from '../subFund/types';
/**
 * --------------------------------------------------
 *	Organisation HTTP Service
 * --------------------------------------------------
 **/
export const FundService = {
  async getOrganizationFunds(
    orgFundsRequest: OrgFundsRequest
  ): Promise<PaginatedResult> {
    const { data } = await HTTPServiceClient.get(
      endpoint.GET_ORGANIZATION_FUNDS(orgFundsRequest)
    );
    return data;
  },

  async getOrganizationPensionSchemes(
    params: PensionSchemeRequest
  ): Promise<PensionScheme[]> {
    const { data } = await HTTPServiceClient.get(
      endpoint.GET_ORGANIZATION_PENSION_SCHEMES(
        params.orgId,
        params.page,
        params.limit,
        params.incompleteFutureReportingPeriod
      )
    );
    return data.data;
  },

  async postSubmissionRequest(
    submissionRequest: SubmissionRequestSendData
  ): Promise<Response> {
    const body = new FormData();
    body.append('due_date', submissionRequest.due_date);
    submissionRequest.investmentIds.forEach(investmentId => {
      body.append('investmentIds[]', investmentId);
    });

    body.append('authorizationLetter', submissionRequest.authorizationLetter);
    body.append('senderOrganizationId', submissionRequest.senderOrganizationId);
    const { data } = await HTTPServiceClient.post(
      endpoint.POST_SUBMISSION_REQUEST,
      body,
      {
        headers: {
          'Content-Type': 'application/form-data'
        }
      }
    );
    return data;
  },

  async deleteSubmissionRequest(
    submissionRequestIds: string[]
  ): Promise<Response> {
    const body = new FormData();
    submissionRequestIds.forEach(id => {
      body.append('submissionRequestIds[]', id);
    });

    const { data } = await HTTPServiceClient.post(
      endpoint.DELETE_SUBMISSION_REQUESTS,
      body,
      {
        headers: {
          'Content-Type': 'application/form-data'
        }
      }
    );
    return data;
  },

  async postAddFund(addFundRequest: AddFundRequest): Promise<Response> {
    const body = FundRequestPayload(addFundRequest);
    const { data } = await HTTPServiceClient.post(
      endpoint.POST_ADD_FUND(addFundRequest.orgId),
      body
    );
    return data;
  },

  async putAddFund(addFundRequest: AddFundRequest): Promise<Response> {
    const body = FundRequestPayload(addFundRequest);
    const { data } = await HTTPServiceClient.put(
      endpoint.PUT_ADD_FUND(addFundRequest.orgId, addFundRequest.id),
      addFundRequest
    );
    return data;
  },

  async putAddFundExtras(
    addFundExtrasRequest: AddFundExtrasRequest
  ): Promise<Response> {
    const { data } = await HTTPServiceClient.put(
      endpoint.PUT_ADD_FUND_EXTRAS(
        addFundExtrasRequest.orgId,
        addFundExtrasRequest.id
      ),
      addFundExtrasRequest
    );
    return data;
  },

  async getStrategies(): Promise<Strategy[]> {
    const { data } = await HTTPServiceClient.get(endpoint.GET_STRATEGY);
    return data.data;
  },

  async getProductTypes(): Promise<ProductType[]> {
    const { data } = await HTTPServiceClient.get(endpoint.GET_PRODUCT_TYPES);
    return data.data;
  },

  async getCurrencies(): Promise<Currency[]> {
    const { data } = await HTTPServiceClient.get(endpoint.GET_CURRENCY);
    return data.data;
  },

  async postAddPortfolio(
    addPortfolioRequest: AddNewPortfolioRequest
  ): Promise<Response> {
    const body = PortfolioRequestPayload(addPortfolioRequest);
    const { data } = await HTTPServiceClient.post(
      endpoint.POST_ADD_PORTFOLIO(addPortfolioRequest.orgId),
      body
    );
    return data;
  },

  async putAddPortfolio(
    addPortfolioRequest: AddNewPortfolioRequest
  ): Promise<Response> {
    const { data } = await HTTPServiceClient.put(
      endpoint.PUT_ADD_PORTFOLIO(
        addPortfolioRequest.orgId,
        addPortfolioRequest.id
      ),
      addPortfolioRequest
    );
    return data;
  },

  async deleteFund(deleteFundRequest: DeleteFundRequest): Promise<Response> {
    const { data } = await HTTPServiceClient.delete(
      endpoint.DELETE_FUND(
        deleteFundRequest.orgId,
        deleteFundRequest.fundId,
        deleteFundRequest.investmentId,
        deleteFundRequest.parentInvestmentId
      )
    );
    return data;
  },

  async deletePortfolio(
    deletePortfolioRequest: DeletePortfolioRequest
  ): Promise<Response> {
    const { data } = await HTTPServiceClient.delete(
      endpoint.DELETE_PORTFOLIO(
        deletePortfolioRequest.orgId,
        deletePortfolioRequest.fundId,
        deletePortfolioRequest.investmentId,
        deletePortfolioRequest.parentInvestmentId
      )
    );
    return data;
  },

  async getCostTemplate(submissionId: string): Promise<CostTemplateResponse> {
    const { data } = await HTTPServiceClient.get(
      endpoint.GET_COST_TEMPLATE(submissionId)
    );
    return data;
  },

  async getAllCostTemplates(
    orgFundsRequest: OrgFundsRequest
  ): Promise<CostTemplateResponse> {
    const { data } = await HTTPServiceClient.post(
      endpoint.GET_ALL_COST_TEMPLATE(orgFundsRequest)
    );
    return data;
  },

  async getBulkJobStatus(jobId: string): Promise<BulkTemplateResponse> {
    const { data } = await HTTPServiceClient.get(
      endpoint.BULK_DOWNLOAD_STATUS(jobId)
    );
    return data;
  },

  async getOrganizationSubFunds(params: SubFundRequest): Promise<SubFund[]> {
    const { data } = await HTTPServiceClient.get(
      endpoint.GET_ORGANIZATION_SUB_FUNDS(
        params.orgId,
        params.page,
        params.limit,
        params.status
      )
    );
    return data.data;
  },

  async getOrganizationManagers(orgId: string): Promise<OrganisationManager[]> {
    const { data } = await HTTPServiceClient.get(
      endpoint.GET_ORGANISATION_MANAGERS(orgId)
    );
    return data.data;
  }
};
